import React, { useCallback, useState } from 'react'
import { XStack, YStack, styled } from 'tamagui'

import { Icon } from '@newComponents/Icon'
import { Text } from '@newComponents/Text'

const CodeContainer = styled(YStack, {
  backgroundColor: '$backgroundHover',
  borderRadius: '$4',
  borderWidth: 1,
  borderColor: '$borderColor',
  overflow: 'hidden',
})

const CodeText = styled(Text, {
  size: '$3',
  color: '$color',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-all',

  style: {
    fontFamily: 'monospace',
  },
})

export function CodeSnippet({ code }: { code: string }) {
  const [copied, setCopied] = useState(false)

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(code)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      const textarea = document.createElement('textarea')
      textarea.value = code
      textarea.style.position = 'fixed'
      textarea.style.opacity = '0'
      document.body.appendChild(textarea)
      textarea.select()
      document.execCommand('copy')
      document.body.removeChild(textarea)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }, [code])

  return (
    <CodeContainer>
      <XStack
        justifyContent='space-between'
        alignItems='center'
        paddingHorizontal='$4'
        paddingVertical='$2'
        borderBottomWidth={1}
        borderBottomColor='$borderColor'
        backgroundColor='$backgroundStrong'
      >
        <Text fontSize='$2' color='$color' fontWeight='$5'>
          Code
        </Text>
        <XStack
          role='button'
          alignItems='center'
          gap='$2'
          cursor='pointer'
          onPress={handleCopy}
          hoverStyle={{ opacity: 0.7 }}
          pressStyle={{ opacity: 0.5 }}
        >
          <Icon
            name={copied ? 'checkmark' : 'copy'}
            size='$3'
            color={copied ? '$successColor' : '$color'}
          />
          <Text
            fontSize='$2'
            color={copied ? '$successColor' : '$color'}
            fontWeight='$5'
          >
            {copied ? 'Copied!' : 'Copy'}
          </Text>
        </XStack>
      </XStack>
      <YStack padding='$4'>
        <CodeText>{code}</CodeText>
      </YStack>
    </CodeContainer>
  )
}
